import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import Swal from 'sweetalert2';

import { ShippingScheduleService } from 'src/app/services/shipping-schedule/shipping-schedule.service';
import { PortService } from 'src/app/services/port/port.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { RegionService } from 'src/app/services/region/region.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';

@Component({
  selector: 'app-ss-add',
  templateUrl: './ss-add.component.html',
  styleUrls: ['./ss-add.component.scss']
})
export class SsAddComponent extends DestructorComponent implements OnInit {

  shippingScheduleForm!: FormGroup;
  isSubmitting: boolean = false;
  isEditMode: boolean = false;
  shippingScheduleId: string | null = null;
  isLoadingData: boolean = false;
  shippingScheduleData: any = null;

  // Region data
  regions: any[] = [];
  isLoadingRegions: boolean = false;

  // Shipping Company search properties
  shippingCompanySearchTerm: string = '';
  shippingCompanySearchResults: any[] = [];
  selectedShippingCompany: any = null;
  showShippingCompanyDropdown: boolean = false;
  isShippingCompanySearching: boolean = false;
  private shippingCompanySearchSubject = new Subject<string>();

  // Port data
  portFromList: any[] = [];
  destinationPortList: any[] = [];
  isLoadingPorts: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private _shippingScheduleService: ShippingScheduleService,
    private _portService: PortService,
    private _vendorService: VendorService,
    private _regionService: RegionService
  ) {
    super();
    this.initializeForm();
    this.setupShippingCompanySearchDebounce();
  }

  ngOnInit(): void {
    // Check if we're in edit mode
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.shippingScheduleId = params['id'];
      }
    });

    // Load regions first
    this.loadRegions();

    // Load shipping schedule data first if in edit mode, then load ports
    if (this.isEditMode && this.shippingScheduleId) {
      this.loadShippingScheduleData();
    } else {
      this.loadPortData();
    }
  }

  private initializeForm(): void {
    this.shippingScheduleForm = this.fb.group({
      region: ['', Validators.required],
      shipping_company_id: ['', Validators.required],
      shipping_company_name: ['', Validators.required],
      vessel: ['', Validators.required],
      type: ['', Validators.required],
      voyage_no: ['', Validators.required],
      port_from_data: this.fb.array([]),
      destination_port_data: this.fb.array([])
    });
  }

  private setupShippingCompanySearchDebounce(): void {
    this.shippingCompanySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchShippingCompany(searchTerm);
    });
  }

  private loadRegions(): void {
    this.isLoadingRegions = true;
    this.subscriptions.push(
      this._regionService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          this.isLoadingRegions = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            const regionData = response.data.data || response.data || [];
            this.regions = regionData.map((region: any) => ({
              value: region.slug,
              label: region.name
            }));
          } else {
            console.error('Invalid region data response:', response);
            this.regions = [];
          }
        },
        error: (error: any) => {
          this.isLoadingRegions = false;
          console.error('Error loading regions:', error);
          this.regions = [];
        }
      })
    );
  }

  private loadPortData(): void {
    this.isLoadingPorts = true;
    this.subscriptions.push(
      this._portService.ShipScheduleList().subscribe({
        next: (response: any) => {
          this.isLoadingPorts = false;
          if (response.status && response.data) {
            this.portFromList = response.data.port_from || [];
            this.destinationPortList = response.data.destination_port || [];

            // Initialize port input values
            this.initializePortInputs();

            // If we have shipping schedule data waiting to be populated, populate it now
            if (this.shippingScheduleData) {
              this.populateFormWithData(this.shippingScheduleData);
              this.shippingScheduleData = null; // Clear the stored data
            }
          } else {
            console.error('Invalid port data response:', response);
            this.portFromList = [];
            this.destinationPortList = [];
          }
        },
        error: (error: any) => {
          this.isLoadingPorts = false;
          console.error('Error loading port data:', error);
          this.portFromList = [];
          this.destinationPortList = [];
        }
      })
    );
  }

  private initializePortInputs(): void {
    // Initialize port from inputs
    this.portFromList.forEach(port => {
      port.inputValue = '';
    });

    // Initialize destination port inputs
    this.destinationPortList.forEach(port => {
      port.inputValue = '';
    });
  }

  private loadShippingScheduleData(): void {
    if (!this.shippingScheduleId) return;

    this.isLoadingData = true;
    this.subscriptions.push(
      this._shippingScheduleService.GetDataById(this.shippingScheduleId).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            // Store the data and load ports
            this.shippingScheduleData = response.data;
            this.loadPortData(); // Load ports after getting shipping schedule data
          } else {
            this.isLoadingData = false;
            Swal.fire({
              title: 'Error!',
              text: 'Failed to load shipping schedule data.',
              icon: 'error',
              confirmButtonText: 'OK'
            }).then(() => {
              this.router.navigate(['/shipping-and-docs/shipping-schedule/listing']);
            });
          }
        },
        error: (error: any) => {
          this.isLoadingData = false;
          console.error('Error loading shipping schedule data:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while loading the shipping schedule.',
            icon: 'error',
            confirmButtonText: 'OK'
          }).then(() => {
            this.router.navigate(['/shipping-and-docs/shipping-schedule/listing']);
          });
        }
      })
    );
  }

  private populateFormWithData(data: any): void {
    // Set loading to false since we're now populating the form
    this.isLoadingData = false;

    // Populate basic form fields
    this.shippingScheduleForm.patchValue({
      region: data.region || '',
      shipping_company_id: data.shipping_company_id || '',
      shipping_company_name: data.shipping_company_name || '',
      vessel: data.vessel || '',
      type: data.type || '',
      voyage_no: data.voyage_no || ''
    });

    // Set shipping company search term and selected company
    if (data.shipping_company_name) {
      this.shippingCompanySearchTerm = data.shipping_company_name;
      this.selectedShippingCompany = {
        _id: data.shipping_company_id,
        company_name: data.shipping_company_name
      };
    }

    // Populate port data if available and port lists are loaded
    if (data.port_from_data && Array.isArray(data.port_from_data) && this.portFromList.length > 0) {
      this.populatePortData(data.port_from_data, this.portFromList);
    }

    if (data.destination_port_data && Array.isArray(data.destination_port_data) && this.destinationPortList.length > 0) {
      this.populatePortData(data.destination_port_data, this.destinationPortList);
    }

    console.log('Form populated with data:', data);
    console.log('Port from list:', this.portFromList);
    console.log('Destination port list:', this.destinationPortList);
  }

  private populatePortData(savedPortData: any[], portList: any[]): void {
    console.log('Populating port data:', savedPortData, 'into port list:', portList);

    savedPortData.forEach(savedPort => {
      const matchingPort = portList.find(port =>
        (port._id || port.id) === (savedPort.port_id || savedPort._id)
      );
      if (matchingPort) {
        matchingPort.inputValue = savedPort.value || '';
        console.log(`Populated port ${matchingPort.name} with value: ${savedPort.value}`);
      } else {
        console.log('No matching port found for:', savedPort);
      }
    });
  }

  // Shipping Company search methods
  onShippingCompanySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.shippingCompanySearchTerm = target.value;
    this.selectedShippingCompany = null;

    // Update the form control with the typed value
    this.shippingScheduleForm.patchValue({
      shipping_company_name: this.shippingCompanySearchTerm,
      shipping_company_id: ''
    });

    if (this.shippingCompanySearchTerm.length >= 2) {
      this.shippingCompanySearchSubject.next(this.shippingCompanySearchTerm);
      this.showShippingCompanyDropdown = true;
    } else {
      this.showShippingCompanyDropdown = false;
      this.shippingCompanySearchResults = [];
    }
  }

  onShippingCompanyInputFocus(): void {
    if (this.shippingCompanySearchResults.length > 0) {
      this.showShippingCompanyDropdown = true;
    }
  }

  onShippingCompanyInputBlur(): void {
    setTimeout(() => {
      this.showShippingCompanyDropdown = false;
    }, 200);
  }

  private searchShippingCompany(searchTerm: string): void {
    this.isShippingCompanySearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'shipping' }).subscribe({
        next: (response: any) => {
          this.isShippingCompanySearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.shippingCompanySearchResults = response.data.data || response.data || [];
            this.showShippingCompanyDropdown = this.shippingCompanySearchResults.length > 0;
          } else {
            this.shippingCompanySearchResults = [];
            this.showShippingCompanyDropdown = false;
          }
        },
        error: (error: any) => {
          this.isShippingCompanySearching = false;
          console.error('Error searching shipping companies:', error);
          this.shippingCompanySearchResults = [];
          this.showShippingCompanyDropdown = false;
        }
      })
    );
  }

  selectShippingCompany(company: any): void {
    this.selectedShippingCompany = company;
    this.shippingCompanySearchTerm = this.getShippingCompanyDisplayName(company);

    this.shippingScheduleForm.patchValue({
      shipping_company_id: company._id || company.id,
      shipping_company_name: this.shippingCompanySearchTerm
    });

    this.showShippingCompanyDropdown = false;
  }

  private getShippingCompanyDisplayName(company: any): string {
    return company.company_name || 'Unknown Company';
  }

  // Port input change handlers
  onPortFromInputChange(port: any, event: Event): void {
    const target = event.target as HTMLInputElement;
    port.inputValue = target.value;
  }

  onDestinationPortInputChange(port: any, event: Event): void {
    const target = event.target as HTMLInputElement;
    port.inputValue = target.value;
  }

  onSubmit(): void {
    if (this.shippingScheduleForm.valid) {
      this.isSubmitting = true;

      // Prepare port data
      const portFromData = this.portFromList
        .filter(port => port.inputValue && port.inputValue.trim() !== '')
        .map(port => ({
          port_id: port._id || port.id,
          port_name: port.name,
          value: port.inputValue
        }));

      const destinationPortData = this.destinationPortList
        .filter(port => port.inputValue && port.inputValue.trim() !== '')
        .map(port => ({
          port_id: port._id || port.id,
          port_name: port.name,
          value: port.inputValue
        }));

      const formData = {
        ...this.shippingScheduleForm.value,
        port_from_data: portFromData,
        destination_port_data: destinationPortData
      };

      // Choose the appropriate service method based on mode
      const serviceCall = this.isEditMode && this.shippingScheduleId
        ? this._shippingScheduleService.UpdateData(this.shippingScheduleId, formData)
        : this._shippingScheduleService.AddData(formData);

      this.subscriptions.push(
        serviceCall.subscribe({
          next: (response: any) => {
            this.isSubmitting = false;
            if (response.status) {
              const successMessage = this.isEditMode
                ? 'Shipping schedule updated successfully.'
                : 'Shipping schedule created successfully.';

              Swal.fire({
                title: 'Success!',
                text: successMessage,
                icon: 'success',
                confirmButtonText: 'OK'
              }).then(() => {
                this.router.navigate(['/shipping-and-documents/shipping-schedule/listing']);
              });
            } else {
              const errorMessage = this.isEditMode
                ? response.message || 'Failed to update shipping schedule.'
                : response.message || 'Failed to create shipping schedule.';

              Swal.fire({
                title: 'Error!',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
          },
          error: (error: any) => {
            this.isSubmitting = false;
            const errorAction = this.isEditMode ? 'updating' : 'creating';
            console.error(`Error ${errorAction} shipping schedule:`, error);

            Swal.fire({
              title: 'Error!',
              text: `An error occurred while ${errorAction} the shipping schedule.`,
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        })
      );
    } else {
      // Show validation errors
      this.markFormGroupTouched(this.shippingScheduleForm);
      Swal.fire({
        title: 'Validation Error!',
        text: 'Please fill in all required fields.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  onCancel(): void {
    this.router.navigate(['/shipping-and-documents/shipping-schedule/listing']);
  }
}
